import { useMemo, useState } from "react";
import type { ElementRef } from "../model/inp/properties.ts";
import type { SimulationResults } from "../model/results/simulationResults.ts";
import {
  seriesFor,
  unitLabelFor,
  variablesFor,
  type ResultVariableKey,
} from "../model/results/resultsVariables.ts";
import { useLanguage, useT } from "../i18n/hooks.ts";
import { localeFor } from "../i18n/language.ts";
import { formatNumber } from "../i18n/formatNumber.ts";
import { ResultsDashboardMiniChart } from "./ResultsDashboardMiniChart.tsx";

type ViewMode = "chart" | "table";

export function ResultsChart({
  results,
  selected,
  flowUnits,
}: {
  results: SimulationResults;
  selected: ElementRef;
  flowUnits: string;
}) {
  const t = useT();
  const language = useLanguage();
  const [view, setView] = useState<ViewMode>("chart");
  const variables = variablesFor(selected.category);
  const [variableKey, setVariableKey] = useState<ResultVariableKey | undefined>(variables[0]?.key);
  // Derived instead of a useEffect, so switching to a category without this
  // key (e.g. node "volume" -> a link) can't flash a stale variableKey.
  const activeKey = variables.some((v) => v.key === variableKey) ? variableKey : variables[0]?.key;
  const series = activeKey ? seriesFor(results, selected, activeKey) : null;

  // Memoized (toLocaleString over every report timestamp is expensive for a
  // long simulation) and computed before the early returns below, since
  // every hook must run on every render (Rules of Hooks).
  const timeLabels = useMemo(
    () =>
      results.times.map((time) =>
        time.toLocaleString(localeFor(language), {
          month: "short", // not "numeric" - avoids an ambiguous date like 1/6
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          timeZone: "UTC", // simulation timestamps are UTC-anchored; display must match
        })
      ),
    [results.times, language]
  );
  const data = useMemo(
    () => (series ? timeLabels.map((time, i) => ({ time, value: series.values[i] })) : []),
    [timeLabels, series]
  );

  if (!activeKey) return null; // raingage has no simulation output of its own
  if (!series) return null;
  const unitLabel = unitLabelFor(activeKey, flowUnits);
  const title = unitLabel ? `${t(series.labelKey)} (${unitLabel})` : t(series.labelKey);

  return (
    <section className="results-chart">
      <div className="results-chart-header">
        <h3>{title}</h3>
        <div className="results-chart-view-toggle" role="tablist">
          <button
            className={view === "chart" ? "button tab-active" : "button"}
            role="tab"
            aria-selected={view === "chart"}
            onClick={() => setView("chart")}
          >
            {t("results.viewChart")}
          </button>
          <button
            className={view === "table" ? "button tab-active" : "button"}
            role="tab"
            aria-selected={view === "table"}
            onClick={() => setView("table")}
          >
            {t("results.viewTable")}
          </button>
        </div>
      </div>
      {variables.length > 1 && (
        <div className="results-chart-view-toggle results-chart-variable-toggle" role="tablist">
          {variables.map((v) => (
            <button
              key={v.key}
              className={activeKey === v.key ? "button tab-active" : "button"}
              role="tab"
              aria-selected={activeKey === v.key}
              onClick={() => setVariableKey(v.key)}
            >
              {t(v.labelKey)}
            </button>
          ))}
        </div>
      )}
      {view === "chart" ? (
        <ResultsDashboardMiniChart data={data} />
      ) : (
        <div className="results-chart-table-wrap">
          <table className="named-collection-points-table">
            <thead>
              <tr>
                <th>{t("results.time")}</th>
                <th>{title}</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row, i) => (
                <tr key={i}>
                  <td className="pattern-table-label-cell">{row.time}</td>
                  <td className="pattern-table-label-cell">
                    {Number.isFinite(row.value) ? formatNumber(row.value, language, 3) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
